/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { remap as remap$ } from "../../lib/primitives.js";
import {
  collectExtraKeys as collectExtraKeys$,
  safeParse,
} from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import * as components from "../components/index.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

export type ListEvalSuiteLeaderboardRunsRequest = {
  suiteId: string;
  suiteVersion?: number | undefined;
  perGroup?: number | undefined;
  model?: string | undefined;
  cursor?: string | undefined;
  /**
   * Optional external end-user identifier forwarded by the API gateway.
   */
  xOnBehalfOf?: string | undefined;
};

export type ListEvalSuiteLeaderboardRunsStop = string | Array<string>;

export const ListEvalSuiteLeaderboardRunsTruncation = {
  Auto: "auto",
  Disabled: "disabled",
} as const;
export type ListEvalSuiteLeaderboardRunsTruncation = ClosedEnum<
  typeof ListEvalSuiteLeaderboardRunsTruncation
>;

export const ListEvalSuiteLeaderboardRunsServiceTier = {
  Auto: "auto",
  Default: "default",
  Flex: "flex",
  Priority: "priority",
} as const;
export type ListEvalSuiteLeaderboardRunsServiceTier = ClosedEnum<
  typeof ListEvalSuiteLeaderboardRunsServiceTier
>;

export type ListEvalSuiteLeaderboardRunsPrefillThink = boolean | string;

export type ListEvalSuiteLeaderboardRunsGeneration = {
  instructions?: string | undefined;
  temperature?: number | undefined;
  topP?: number | undefined;
  maxOutputTokens?: number | undefined;
  /**
   * lm-eval alias for max_output_tokens.
   */
  maxGenToks?: number | undefined;
  stop?: string | Array<string> | undefined;
  /**
   * lm-eval generate_until stop sequences.
   */
  until?: Array<string> | undefined;
  maxToolCalls?: number | undefined;
  reasoning?: any | undefined;
  text?: any | undefined;
  tools?: Array<any | null> | undefined;
  toolChoice?: any | undefined;
  parallelToolCalls?: boolean | undefined;
  truncation?: ListEvalSuiteLeaderboardRunsTruncation | undefined;
  serviceTier?: ListEvalSuiteLeaderboardRunsServiceTier | undefined;
  presencePenalty?: number | undefined;
  frequencyPenalty?: number | undefined;
  topK?: number | undefined;
  minP?: number | undefined;
  repetitionPenalty?: number | undefined;
  doSample?: boolean | undefined;
  extraBody?: { [k: string]: any } | undefined;
  chatTemplateKwargs?: { [k: string]: any } | undefined;
  prefillThink?: boolean | string | undefined;
  useCache?: boolean | undefined;
  timeoutSeconds?: number | undefined;
  maxRetries?: number | undefined;
  maxEmptyRetries?: number | undefined;
  additionalProperties?: { [k: string]: any } | undefined;
};

export type ListEvalSuiteLeaderboardRunsRequestCounts = {
  total: number;
  completed: number;
  failed: number;
};

export type ListEvalSuiteLeaderboardRunsData = {
  id: string;
  object: "eval.run";
  suiteId: string;
  suiteVersion: number;
  suiteVersionId: string;
  /**
   * The org that owns this run.
   */
  orgId: string;
  /**
   * The team that owns this run.
   */
  teamId: string;
  status: components.EvalRunStatus;
  models: Array<string>;
  taskIds: Array<string> | null;
  judgeModel: string | null;
  embeddingModel: string | null;
  generation: ListEvalSuiteLeaderboardRunsGeneration;
  requestCounts: ListEvalSuiteLeaderboardRunsRequestCounts;
  metrics: { [k: string]: any } | null;
  error: { [k: string]: any } | null;
  artifactFileIds: Array<string>;
  metadata: { [k: string]: string };
  createdAt: number;
  startedAt: number | null;
  completedAt: number | null;
  cancelledAt: number | null;
  failedAt: number | null;
};

/**
 * OK
 */
export type ListEvalSuiteLeaderboardRunsResponseBody = {
  object: "eval.leaderboard_runs";
  /**
   * The deduplicated runs behind `groups`, newest first.
   */
  data: Array<ListEvalSuiteLeaderboardRunsData>;
  /**
   * The server's group assignments — build boards from these, not from run.models/run.task_ids. Ordered by model, then task id with the covers-everything group first within each model, then rank; that ordering is contract.
   */
  groups: Array<components.EvalLeaderboardRunGroup>;
  /**
   * True when a server ceiling trimmed the aggregate. Whole runs are kept in (rank, model, task) order — never dropped for being old; resume with `cursor: next_cursor`.
   */
  truncated: boolean;
  /**
   * Set when truncated: pass as `cursor` on the next request and union the responses until truncated is false to assemble the complete aggregate. Null when this response is complete.
   */
  nextCursor: string | null;
};

/** @internal */
export const ListEvalSuiteLeaderboardRunsRequest$inboundSchema: z.ZodType<
  ListEvalSuiteLeaderboardRunsRequest,
  z.ZodTypeDef,
  unknown
> = z.object({
  suite_id: z.string(),
  suite_version: z.number().int().optional(),
  per_group: z.number().int().default(5),
  model: z.string().optional(),
  cursor: z.string().optional(),
  "X-On-Behalf-Of": z.string().optional(),
}).transform((v) => {
  return remap$(v, {
    "suite_id": "suiteId",
    "suite_version": "suiteVersion",
    "per_group": "perGroup",
    "X-On-Behalf-Of": "xOnBehalfOf",
  });
});
/** @internal */
export type ListEvalSuiteLeaderboardRunsRequest$Outbound = {
  suite_id: string;
  suite_version?: number | undefined;
  per_group: number;
  model?: string | undefined;
  cursor?: string | undefined;
  "X-On-Behalf-Of"?: string | undefined;
};

/** @internal */
export const ListEvalSuiteLeaderboardRunsRequest$outboundSchema: z.ZodType<
  ListEvalSuiteLeaderboardRunsRequest$Outbound,
  z.ZodTypeDef,
  ListEvalSuiteLeaderboardRunsRequest
> = z.object({
  suiteId: z.string(),
  suiteVersion: z.number().int().optional(),
  perGroup: z.number().int().default(5),
  model: z.string().optional(),
  cursor: z.string().optional(),
  xOnBehalfOf: z.string().optional(),
}).transform((v) => {
  return remap$(v, {
    suiteId: "suite_id",
    suiteVersion: "suite_version",
    perGroup: "per_group",
    xOnBehalfOf: "X-On-Behalf-Of",
  });
});

export function listEvalSuiteLeaderboardRunsRequestToJSON(
  listEvalSuiteLeaderboardRunsRequest: ListEvalSuiteLeaderboardRunsRequest,
): string {
  return JSON.stringify(
    ListEvalSuiteLeaderboardRunsRequest$outboundSchema.parse(
      listEvalSuiteLeaderboardRunsRequest,
    ),
  );
}
export function listEvalSuiteLeaderboardRunsRequestFromJSON(
  jsonString: string,
): SafeParseResult<ListEvalSuiteLeaderboardRunsRequest, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) =>
      ListEvalSuiteLeaderboardRunsRequest$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ListEvalSuiteLeaderboardRunsRequest' from JSON`,
  );
}

/** @internal */
export const ListEvalSuiteLeaderboardRunsStop$inboundSchema: z.ZodType<
  ListEvalSuiteLeaderboardRunsStop,
  z.ZodTypeDef,
  unknown
> = z.union([z.string(), z.array(z.string())]);
/** @internal */
export type ListEvalSuiteLeaderboardRunsStop$Outbound = string | Array<string>;

/** @internal */
export const ListEvalSuiteLeaderboardRunsStop$outboundSchema: z.ZodType<
  ListEvalSuiteLeaderboardRunsStop$Outbound,
  z.ZodTypeDef,
  ListEvalSuiteLeaderboardRunsStop
> = z.union([z.string(), z.array(z.string())]);

export function listEvalSuiteLeaderboardRunsStopToJSON(
  listEvalSuiteLeaderboardRunsStop: ListEvalSuiteLeaderboardRunsStop,
): string {
  return JSON.stringify(
    ListEvalSuiteLeaderboardRunsStop$outboundSchema.parse(
      listEvalSuiteLeaderboardRunsStop,
    ),
  );
}
export function listEvalSuiteLeaderboardRunsStopFromJSON(
  jsonString: string,
): SafeParseResult<ListEvalSuiteLeaderboardRunsStop, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ListEvalSuiteLeaderboardRunsStop$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ListEvalSuiteLeaderboardRunsStop' from JSON`,
  );
}

/** @internal */
export const ListEvalSuiteLeaderboardRunsTruncation$inboundSchema:
  z.ZodNativeEnum<typeof ListEvalSuiteLeaderboardRunsTruncation> = z.nativeEnum(
    ListEvalSuiteLeaderboardRunsTruncation,
  );
/** @internal */
export const ListEvalSuiteLeaderboardRunsTruncation$outboundSchema:
  z.ZodNativeEnum<typeof ListEvalSuiteLeaderboardRunsTruncation> =
    ListEvalSuiteLeaderboardRunsTruncation$inboundSchema;

/** @internal */
export const ListEvalSuiteLeaderboardRunsServiceTier$inboundSchema:
  z.ZodNativeEnum<typeof ListEvalSuiteLeaderboardRunsServiceTier> = z
    .nativeEnum(ListEvalSuiteLeaderboardRunsServiceTier);
/** @internal */
export const ListEvalSuiteLeaderboardRunsServiceTier$outboundSchema:
  z.ZodNativeEnum<typeof ListEvalSuiteLeaderboardRunsServiceTier> =
    ListEvalSuiteLeaderboardRunsServiceTier$inboundSchema;

/** @internal */
export const ListEvalSuiteLeaderboardRunsPrefillThink$inboundSchema: z.ZodType<
  ListEvalSuiteLeaderboardRunsPrefillThink,
  z.ZodTypeDef,
  unknown
> = z.union([z.boolean(), z.string()]);
/** @internal */
export type ListEvalSuiteLeaderboardRunsPrefillThink$Outbound =
  | boolean
  | string;

/** @internal */
export const ListEvalSuiteLeaderboardRunsPrefillThink$outboundSchema: z.ZodType<
  ListEvalSuiteLeaderboardRunsPrefillThink$Outbound,
  z.ZodTypeDef,
  ListEvalSuiteLeaderboardRunsPrefillThink
> = z.union([z.boolean(), z.string()]);

export function listEvalSuiteLeaderboardRunsPrefillThinkToJSON(
  listEvalSuiteLeaderboardRunsPrefillThink:
    ListEvalSuiteLeaderboardRunsPrefillThink,
): string {
  return JSON.stringify(
    ListEvalSuiteLeaderboardRunsPrefillThink$outboundSchema.parse(
      listEvalSuiteLeaderboardRunsPrefillThink,
    ),
  );
}
export function listEvalSuiteLeaderboardRunsPrefillThinkFromJSON(
  jsonString: string,
): SafeParseResult<
  ListEvalSuiteLeaderboardRunsPrefillThink,
  SDKValidationError
> {
  return safeParse(
    jsonString,
    (x) =>
      ListEvalSuiteLeaderboardRunsPrefillThink$inboundSchema.parse(
        JSON.parse(x),
      ),
    `Failed to parse 'ListEvalSuiteLeaderboardRunsPrefillThink' from JSON`,
  );
}

/** @internal */
export const ListEvalSuiteLeaderboardRunsGeneration$inboundSchema: z.ZodType<
  ListEvalSuiteLeaderboardRunsGeneration,
  z.ZodTypeDef,
  unknown
> = collectExtraKeys$(
  z.object({
    instructions: z.string().optional(),
    temperature: z.number().optional(),
    top_p: z.number().optional(),
    max_output_tokens: z.number().int().optional(),
    max_gen_toks: z.number().int().optional(),
    stop: z.union([z.string(), z.array(z.string())]).optional(),
    until: z.array(z.string()).optional(),
    max_tool_calls: z.number().int().optional(),
    reasoning: z.any().optional(),
    text: z.any().optional(),
    tools: z.array(z.nullable(z.any())).optional(),
    tool_choice: z.any().optional(),
    parallel_tool_calls: z.boolean().optional(),
    truncation: ListEvalSuiteLeaderboardRunsTruncation$inboundSchema.optional(),
    service_tier: ListEvalSuiteLeaderboardRunsServiceTier$inboundSchema
      .optional(),
    presence_penalty: z.number().optional(),
    frequency_penalty: z.number().optional(),
    top_k: z.number().int().optional(),
    min_p: z.number().optional(),
    repetition_penalty: z.number().optional(),
    do_sample: z.boolean().optional(),
    extra_body: z.record(z.any()).optional(),
    chat_template_kwargs: z.record(z.any()).optional(),
    prefill_think: z.union([z.boolean(), z.string()]).optional(),
    use_cache: z.boolean().optional(),
    timeout_seconds: z.number().int().optional(),
    max_retries: z.number().int().optional(),
    max_empty_retries: z.number().int().optional(),
  }).catchall(z.any()),
  "additionalProperties",
  true,
).transform((v) => {
  return remap$(v, {
    "top_p": "topP",
    "max_output_tokens": "maxOutputTokens",
    "max_gen_toks": "maxGenToks",
    "max_tool_calls": "maxToolCalls",
    "tool_choice": "toolChoice",
    "parallel_tool_calls": "parallelToolCalls",
    "service_tier": "serviceTier",
    "presence_penalty": "presencePenalty",
    "frequency_penalty": "frequencyPenalty",
    "top_k": "topK",
    "min_p": "minP",
    "repetition_penalty": "repetitionPenalty",
    "do_sample": "doSample",
    "extra_body": "extraBody",
    "chat_template_kwargs": "chatTemplateKwargs",
    "prefill_think": "prefillThink",
    "use_cache": "useCache",
    "timeout_seconds": "timeoutSeconds",
    "max_retries": "maxRetries",
    "max_empty_retries": "maxEmptyRetries",
  });
});
/** @internal */
export type ListEvalSuiteLeaderboardRunsGeneration$Outbound = {
  instructions?: string | undefined;
  temperature?: number | undefined;
  top_p?: number | undefined;
  max_output_tokens?: number | undefined;
  max_gen_toks?: number | undefined;
  stop?: string | Array<string> | undefined;
  until?: Array<string> | undefined;
  max_tool_calls?: number | undefined;
  reasoning?: any | undefined;
  text?: any | undefined;
  tools?: Array<any | null> | undefined;
  tool_choice?: any | undefined;
  parallel_tool_calls?: boolean | undefined;
  truncation?: string | undefined;
  service_tier?: string | undefined;
  presence_penalty?: number | undefined;
  frequency_penalty?: number | undefined;
  top_k?: number | undefined;
  min_p?: number | undefined;
  repetition_penalty?: number | undefined;
  do_sample?: boolean | undefined;
  extra_body?: { [k: string]: any } | undefined;
  chat_template_kwargs?: { [k: string]: any } | undefined;
  prefill_think?: boolean | string | undefined;
  use_cache?: boolean | undefined;
  timeout_seconds?: number | undefined;
  max_retries?: number | undefined;
  max_empty_retries?: number | undefined;
  [additionalProperties: string]: unknown;
};

/** @internal */
export const ListEvalSuiteLeaderboardRunsGeneration$outboundSchema: z.ZodType<
  ListEvalSuiteLeaderboardRunsGeneration$Outbound,
  z.ZodTypeDef,
  ListEvalSuiteLeaderboardRunsGeneration
> = z.object({
  instructions: z.string().optional(),
  temperature: z.number().optional(),
  topP: z.number().optional(),
  maxOutputTokens: z.number().int().optional(),
  maxGenToks: z.number().int().optional(),
  stop: z.union([z.string(), z.array(z.string())]).optional(),
  until: z.array(z.string()).optional(),
  maxToolCalls: z.number().int().optional(),
  reasoning: z.any().optional(),
  text: z.any().optional(),
  tools: z.array(z.nullable(z.any())).optional(),
  toolChoice: z.any().optional(),
  parallelToolCalls: z.boolean().optional(),
  truncation: ListEvalSuiteLeaderboardRunsTruncation$outboundSchema.optional(),
  serviceTier: ListEvalSuiteLeaderboardRunsServiceTier$outboundSchema
    .optional(),
  presencePenalty: z.number().optional(),
  frequencyPenalty: z.number().optional(),
  topK: z.number().int().optional(),
  minP: z.number().optional(),
  repetitionPenalty: z.number().optional(),
  doSample: z.boolean().optional(),
  extraBody: z.record(z.any()).optional(),
  chatTemplateKwargs: z.record(z.any()).optional(),
  prefillThink: z.union([z.boolean(), z.string()]).optional(),
  useCache: z.boolean().optional(),
  timeoutSeconds: z.number().int().optional(),
  maxRetries: z.number().int().optional(),
  maxEmptyRetries: z.number().int().optional(),
  additionalProperties: z.record(z.any()).optional(),
}).transform((v) => {
  return {
    ...v.additionalProperties,
    ...remap$(v, {
      topP: "top_p",
      maxOutputTokens: "max_output_tokens",
      maxGenToks: "max_gen_toks",
      maxToolCalls: "max_tool_calls",
      toolChoice: "tool_choice",
      parallelToolCalls: "parallel_tool_calls",
      serviceTier: "service_tier",
      presencePenalty: "presence_penalty",
      frequencyPenalty: "frequency_penalty",
      topK: "top_k",
      minP: "min_p",
      repetitionPenalty: "repetition_penalty",
      doSample: "do_sample",
      extraBody: "extra_body",
      chatTemplateKwargs: "chat_template_kwargs",
      prefillThink: "prefill_think",
      useCache: "use_cache",
      timeoutSeconds: "timeout_seconds",
      maxRetries: "max_retries",
      maxEmptyRetries: "max_empty_retries",
      additionalProperties: null,
    }),
  };
});

export function listEvalSuiteLeaderboardRunsGenerationToJSON(
  listEvalSuiteLeaderboardRunsGeneration:
    ListEvalSuiteLeaderboardRunsGeneration,
): string {
  return JSON.stringify(
    ListEvalSuiteLeaderboardRunsGeneration$outboundSchema.parse(
      listEvalSuiteLeaderboardRunsGeneration,
    ),
  );
}
export function listEvalSuiteLeaderboardRunsGenerationFromJSON(
  jsonString: string,
): SafeParseResult<ListEvalSuiteLeaderboardRunsGeneration, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) =>
      ListEvalSuiteLeaderboardRunsGeneration$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ListEvalSuiteLeaderboardRunsGeneration' from JSON`,
  );
}

/** @internal */
export const ListEvalSuiteLeaderboardRunsRequestCounts$inboundSchema: z.ZodType<
  ListEvalSuiteLeaderboardRunsRequestCounts,
  z.ZodTypeDef,
  unknown
> = z.object({
  total: z.number().int(),
  completed: z.number().int(),
  failed: z.number().int(),
});
/** @internal */
export type ListEvalSuiteLeaderboardRunsRequestCounts$Outbound = {
  total: number;
  completed: number;
  failed: number;
};

/** @internal */
export const ListEvalSuiteLeaderboardRunsRequestCounts$outboundSchema:
  z.ZodType<
    ListEvalSuiteLeaderboardRunsRequestCounts$Outbound,
    z.ZodTypeDef,
    ListEvalSuiteLeaderboardRunsRequestCounts
  > = z.object({
    total: z.number().int(),
    completed: z.number().int(),
    failed: z.number().int(),
  });

export function listEvalSuiteLeaderboardRunsRequestCountsToJSON(
  listEvalSuiteLeaderboardRunsRequestCounts:
    ListEvalSuiteLeaderboardRunsRequestCounts,
): string {
  return JSON.stringify(
    ListEvalSuiteLeaderboardRunsRequestCounts$outboundSchema.parse(
      listEvalSuiteLeaderboardRunsRequestCounts,
    ),
  );
}
export function listEvalSuiteLeaderboardRunsRequestCountsFromJSON(
  jsonString: string,
): SafeParseResult<
  ListEvalSuiteLeaderboardRunsRequestCounts,
  SDKValidationError
> {
  return safeParse(
    jsonString,
    (x) =>
      ListEvalSuiteLeaderboardRunsRequestCounts$inboundSchema.parse(
        JSON.parse(x),
      ),
    `Failed to parse 'ListEvalSuiteLeaderboardRunsRequestCounts' from JSON`,
  );
}

/** @internal */
export const ListEvalSuiteLeaderboardRunsData$inboundSchema: z.ZodType<
  ListEvalSuiteLeaderboardRunsData,
  z.ZodTypeDef,
  unknown
> = z.object({
  id: z.string(),
  object: z.literal("eval.run"),
  suite_id: z.string(),
  suite_version: z.number().int(),
  suite_version_id: z.string(),
  org_id: z.string(),
  team_id: z.string(),
  status: components.EvalRunStatus$inboundSchema,
  models: z.array(z.string()),
  task_ids: z.nullable(z.array(z.string())),
  judge_model: z.nullable(z.string()),
  embedding_model: z.nullable(z.string()),
  generation: z.lazy(() =>
    ListEvalSuiteLeaderboardRunsGeneration$inboundSchema
  ),
  request_counts: z.lazy(() =>
    ListEvalSuiteLeaderboardRunsRequestCounts$inboundSchema
  ),
  metrics: z.nullable(z.record(z.any())),
  error: z.nullable(z.record(z.any())),
  artifact_file_ids: z.array(z.string()),
  metadata: z.record(z.string()),
  created_at: z.number().int(),
  started_at: z.nullable(z.number().int()),
  completed_at: z.nullable(z.number().int()),
  cancelled_at: z.nullable(z.number().int()),
  failed_at: z.nullable(z.number().int()),
}).transform((v) => {
  return remap$(v, {
    "suite_id": "suiteId",
    "suite_version": "suiteVersion",
    "suite_version_id": "suiteVersionId",
    "org_id": "orgId",
    "team_id": "teamId",
    "task_ids": "taskIds",
    "judge_model": "judgeModel",
    "embedding_model": "embeddingModel",
    "request_counts": "requestCounts",
    "artifact_file_ids": "artifactFileIds",
    "created_at": "createdAt",
    "started_at": "startedAt",
    "completed_at": "completedAt",
    "cancelled_at": "cancelledAt",
    "failed_at": "failedAt",
  });
});
/** @internal */
export type ListEvalSuiteLeaderboardRunsData$Outbound = {
  id: string;
  object: "eval.run";
  suite_id: string;
  suite_version: number;
  suite_version_id: string;
  org_id: string;
  team_id: string;
  status: string;
  models: Array<string>;
  task_ids: Array<string> | null;
  judge_model: string | null;
  embedding_model: string | null;
  generation: ListEvalSuiteLeaderboardRunsGeneration$Outbound;
  request_counts: ListEvalSuiteLeaderboardRunsRequestCounts$Outbound;
  metrics: { [k: string]: any } | null;
  error: { [k: string]: any } | null;
  artifact_file_ids: Array<string>;
  metadata: { [k: string]: string };
  created_at: number;
  started_at: number | null;
  completed_at: number | null;
  cancelled_at: number | null;
  failed_at: number | null;
};

/** @internal */
export const ListEvalSuiteLeaderboardRunsData$outboundSchema: z.ZodType<
  ListEvalSuiteLeaderboardRunsData$Outbound,
  z.ZodTypeDef,
  ListEvalSuiteLeaderboardRunsData
> = z.object({
  id: z.string(),
  object: z.literal("eval.run"),
  suiteId: z.string(),
  suiteVersion: z.number().int(),
  suiteVersionId: z.string(),
  orgId: z.string(),
  teamId: z.string(),
  status: components.EvalRunStatus$outboundSchema,
  models: z.array(z.string()),
  taskIds: z.nullable(z.array(z.string())),
  judgeModel: z.nullable(z.string()),
  embeddingModel: z.nullable(z.string()),
  generation: z.lazy(() =>
    ListEvalSuiteLeaderboardRunsGeneration$outboundSchema
  ),
  requestCounts: z.lazy(() =>
    ListEvalSuiteLeaderboardRunsRequestCounts$outboundSchema
  ),
  metrics: z.nullable(z.record(z.any())),
  error: z.nullable(z.record(z.any())),
  artifactFileIds: z.array(z.string()),
  metadata: z.record(z.string()),
  createdAt: z.number().int(),
  startedAt: z.nullable(z.number().int()),
  completedAt: z.nullable(z.number().int()),
  cancelledAt: z.nullable(z.number().int()),
  failedAt: z.nullable(z.number().int()),
}).transform((v) => {
  return remap$(v, {
    suiteId: "suite_id",
    suiteVersion: "suite_version",
    suiteVersionId: "suite_version_id",
    orgId: "org_id",
    teamId: "team_id",
    taskIds: "task_ids",
    judgeModel: "judge_model",
    embeddingModel: "embedding_model",
    requestCounts: "request_counts",
    artifactFileIds: "artifact_file_ids",
    createdAt: "created_at",
    startedAt: "started_at",
    completedAt: "completed_at",
    cancelledAt: "cancelled_at",
    failedAt: "failed_at",
  });
});

export function listEvalSuiteLeaderboardRunsDataToJSON(
  listEvalSuiteLeaderboardRunsData: ListEvalSuiteLeaderboardRunsData,
): string {
  return JSON.stringify(
    ListEvalSuiteLeaderboardRunsData$outboundSchema.parse(
      listEvalSuiteLeaderboardRunsData,
    ),
  );
}
export function listEvalSuiteLeaderboardRunsDataFromJSON(
  jsonString: string,
): SafeParseResult<ListEvalSuiteLeaderboardRunsData, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ListEvalSuiteLeaderboardRunsData$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ListEvalSuiteLeaderboardRunsData' from JSON`,
  );
}

/** @internal */
export const ListEvalSuiteLeaderboardRunsResponseBody$inboundSchema: z.ZodType<
  ListEvalSuiteLeaderboardRunsResponseBody,
  z.ZodTypeDef,
  unknown
> = z.object({
  object: z.literal("eval.leaderboard_runs"),
  data: z.array(z.lazy(() => ListEvalSuiteLeaderboardRunsData$inboundSchema)),
  groups: z.array(components.EvalLeaderboardRunGroup$inboundSchema),
  truncated: z.boolean(),
  next_cursor: z.nullable(z.string()),
}).transform((v) => {
  return remap$(v, {
    "next_cursor": "nextCursor",
  });
});
/** @internal */
export type ListEvalSuiteLeaderboardRunsResponseBody$Outbound = {
  object: "eval.leaderboard_runs";
  data: Array<ListEvalSuiteLeaderboardRunsData$Outbound>;
  groups: Array<components.EvalLeaderboardRunGroup$Outbound>;
  truncated: boolean;
  next_cursor: string | null;
};

/** @internal */
export const ListEvalSuiteLeaderboardRunsResponseBody$outboundSchema: z.ZodType<
  ListEvalSuiteLeaderboardRunsResponseBody$Outbound,
  z.ZodTypeDef,
  ListEvalSuiteLeaderboardRunsResponseBody
> = z.object({
  object: z.literal("eval.leaderboard_runs"),
  data: z.array(z.lazy(() => ListEvalSuiteLeaderboardRunsData$outboundSchema)),
  groups: z.array(components.EvalLeaderboardRunGroup$outboundSchema),
  truncated: z.boolean(),
  nextCursor: z.nullable(z.string()),
}).transform((v) => {
  return remap$(v, {
    nextCursor: "next_cursor",
  });
});

export function listEvalSuiteLeaderboardRunsResponseBodyToJSON(
  listEvalSuiteLeaderboardRunsResponseBody:
    ListEvalSuiteLeaderboardRunsResponseBody,
): string {
  return JSON.stringify(
    ListEvalSuiteLeaderboardRunsResponseBody$outboundSchema.parse(
      listEvalSuiteLeaderboardRunsResponseBody,
    ),
  );
}
export function listEvalSuiteLeaderboardRunsResponseBodyFromJSON(
  jsonString: string,
): SafeParseResult<
  ListEvalSuiteLeaderboardRunsResponseBody,
  SDKValidationError
> {
  return safeParse(
    jsonString,
    (x) =>
      ListEvalSuiteLeaderboardRunsResponseBody$inboundSchema.parse(
        JSON.parse(x),
      ),
    `Failed to parse 'ListEvalSuiteLeaderboardRunsResponseBody' from JSON`,
  );
}
